import { useState, useEffect } from "react";
import { Coins, Timer, Sparkles } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import TipJar from "./TipJar";

export default function JarWidget({ onContribute }) {
  const { user } = useAuth();
  const [jar, setJar] = useState(null);
  const [distributing, setDistributing] = useState(false);
  const [countdown, setCountdown] = useState("");

  const load = () => api.get("/jar").then((r) => setJar(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const poll = setInterval(load, 15000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(poll); };
  }, []);

  useEffect(() => {
    if (!jar?.next_distribution_at) return;
    const tick = () => {
      const diff = new Date(jar.next_distribution_at).getTime() - Date.now();
      if (diff <= 0) { setCountdown("splitting soon…"); load(); return; }
      const h = Math.floor(diff / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4), s = Math.floor((diff % 6e4) / 1000);
      setCountdown(`${h}h ${m}m ${s}s`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [jar?.next_distribution_at]);

  const distribute = async () => {
    setDistributing(true);
    try {
      const { data } = await api.post("/jar/distribute");
      toast.success(`$${data.distributed.toFixed(2)} split among ${data.recipients} members — $${data.share.toFixed(2)} each! 🎉`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    setDistributing(false);
  };

  return (
    <div data-testid="community-tip-jar-widget" className="bg-card rounded-3xl border border-border p-6 sm:p-8 shadow-lg relative overflow-hidden">
      <div className="absolute -top-8 -right-8 w-40 h-40 bg-penny/10 rounded-full blur-2xl" />
      <div className="flex items-center gap-2 text-copper font-mono text-xs uppercase tracking-widest justify-center sm:justify-start">
        <Coins className="w-4 h-4" /> Community Tip Jar
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center gap-5">
        <TipJar balance={jar ? jar.balance : 0} className="w-32 sm:w-36 h-auto shrink-0 drop-shadow-sm" />
        <div className="flex-1 w-full text-center sm:text-left">
          <div className="flex items-end gap-2 justify-center sm:justify-start">
            <span className="font-serif text-5xl sm:text-6xl font-bold text-bronze tabular-nums">
              ${jar ? jar.balance.toFixed(2) : "—"}
            </span>
            <span className="text-muted-foreground mb-2 text-sm">in the jar</span>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="bg-secondary rounded-2xl p-3">
              <p className="text-xs text-muted-foreground">Seeking support</p>
              <p className="font-serif text-2xl font-semibold text-bronze">{jar?.seeker_count ?? "—"}</p>
            </div>
            <div className="bg-secondary rounded-2xl p-3">
              <p className="text-xs text-muted-foreground">Each gets ~</p>
              <p className="font-serif text-2xl font-semibold text-bronze">${jar ? jar.per_person_estimate.toFixed(2) : "—"}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 bg-penny/10 rounded-2xl p-3">
        <Timer className="w-5 h-5 text-penny shrink-0" />
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">Automatic split in</p>
          <p className="font-mono font-bold text-bronze tabular-nums" data-testid="jar-countdown">{countdown || "—"}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Given so far</p>
          <p className="font-serif font-semibold text-bronze">${jar ? jar.total_distributed.toFixed(2) : "—"}</p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Every 24 hours the jar splits itself equally among all members seeking support — automatically, no one has to press a thing.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <Button data-testid="jar-contribute-btn" onClick={onContribute} className="flex-1 bg-copper hover:bg-copper/90 text-white rounded-full py-6">
          <Coins className="w-4 h-4 mr-2" /> Drop in the jar
        </Button>
        {user && (
          <Button data-testid="jar-distribute-btn" onClick={distribute} disabled={distributing} variant="outline"
            className="flex-1 rounded-full py-6 border-penny/50 text-bronze hover:bg-penny/10">
            <Sparkles className="w-4 h-4 mr-2" /> {distributing ? "Splitting…" : "Distribute now"}
          </Button>
        )}
      </div>
    </div>
  );
}
